import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PatientSidebar from "../../components/PatientSidebar";
import WeeklyFormModal from "../../components/weeklyFormModal";
import Roadtrip from "../../components/roadtrip";
import socket from "../../socket";
import { onFormSaved } from "../../formEvents";
import { pathPatientActivityPlan, pathPatientCaregiverSupport, API_URL } from "../../config/constants";
import activityCardImages from "../../assets/activity_cards/images";
import ImgTherapist      from "../../assets/therapist.png";
import ImgQuestionnaire  from "../../assets/questionnaire.png";
import ImgWeeklyFormLocked from "../../assets/weekly_form_locked.png";
import ImgWeeklyFormDone   from "../../assets/weekly_form_done.png";
import TopBar from "../../components/TopBar";
import {
  Layout, Main,
  SectionTitle,
  CardsRow, ActivityCard, CardIllustration, CardImage, CardBody, CardTypeName, CardSubtext,
  CardButton,
  BottomRow, Panel, PanelWide, PanelTitle, PanelButton,
  TherapistAvatar, MessageText,
  QuestionnaireIcon, QuestionnaireTitle, QuestionnaireText,
} from "./styles";

// La imagen de cada tarjeta sale de activityCardImages por el mismo id.
const ACTIVITY_TYPES = [
  { id: 1, name: "Motor" },
  { id: 2, name: "Self-regulation" },
  { id: 3, name: "Cognitive" },
  { id: 4, name: "Sensory" },
  { id: 5, name: "Interactive-communicative" },
];

// Contenido de la tarjeta según el `status` de GET /me/weekly-form.
const WEEKLY_FORM_CARD = {
  not_available: { img: ImgWeeklyFormLocked, title: "notAvailableTitle", text: "notAvailableText", button: "notAvailableButton", disabled: true },
  ready:         { img: ImgQuestionnaire,    title: "readyTitle",        text: "readyText",        button: "fillForm" },
  completed:     { img: ImgWeeklyFormDone,   title: "doneTitle",         text: "doneText",         button: "reviewForm", outline: true },
  finished:      { img: ImgWeeklyFormDone,   title: "doneTitle",         text: "finishedText",     button: "notAvailableButton", disabled: true },
};

const PatientHome = () => {
  const { t } = useTranslation();
  const history      = useHistory();
  const token        = localStorage.getItem("token");

  const [countByType, setCountByType] = useState({});
  const [therapist, setTherapist] = useState(null);
  const [weeklyForm, setWeeklyForm] = useState(null);
  const [showWeeklyForm, setShowWeeklyForm] = useState(false);
  const weeklyCard = weeklyForm && WEEKLY_FORM_CARD[weeklyForm.status];


  useEffect(() => {
    fetch(`${API_URL}/me/activity-plan`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const counts = {};
        data.forEach((a) => {
          counts[a.activity_type_id] = (counts[a.activity_type_id] || 0) + 1;
        });
        setCountByType(counts);
      })
      .catch((err) => console.error("PatientHome activity-plan fetch error:", err));

    fetch(`${API_URL}/me/therapist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => { if (!data.error) setTherapist(data); })
      .catch(() => {});

    fetch(`${API_URL}/me/weekly-form`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => { if (!data.error) setWeeklyForm(data); })
      .catch(() => {});
  }, []); // eslint-disable-line

  // El terapeuta puede editar el mensaje fijo mientras esta pantalla está abierta.
  useEffect(() => {
    const onMessageUpdated = (data) =>
      setTherapist((prev) => (prev ? { ...prev, therapist_message: data.therapist_message } : prev));
    socket.on("therapistMessageUpdated", onMessageUpdated);
    return () => socket.off("therapistMessageUpdated", onMessageUpdated);
  }, []);

  // El formulario se puede enviar desde aquí o desde "Pending forms" de la barra lateral.
  useEffect(
    () => onFormSaved(({ type, state }) => { if (type === "weekly") setWeeklyForm(state); }),
    []
  );


  return (
    <Layout>
      <TopBar />

      <PatientSidebar activeNav="home" />

      <Main>
        <SectionTitle>{t("patientHome.weeklyActivities")}</SectionTitle>

        <CardsRow>
          {ACTIVITY_TYPES.map((type) => {
            // type.name es el nombre fuente en ingles; el que se muestra sale
            // de i18n para que cambie con el idioma de la interfaz.
            const typeName = t(`activityTypes.${type.id}.name`, { defaultValue: type.name });
            return (
              <ActivityCard key={type.id} onClick={() => history.push(pathPatientActivityPlan, { typeId: type.id })}>
                <CardIllustration>
                  <CardImage src={activityCardImages[type.id]} alt={typeName} />
                </CardIllustration>
                <CardBody>
                  <CardTypeName>{typeName}</CardTypeName>
                  <CardSubtext>{t("patientHome.activitiesCount", { total: countByType[type.id] ?? 0 })}</CardSubtext>

                  <CardButton>{t("patientHome.typeActivities", { type: typeName })}</CardButton>
                </CardBody>
              </ActivityCard>
            );
          })}
        </CardsRow>

        <BottomRow>
          <Panel>
            <PanelTitle>{t("patientHome.therapistMessage")}</PanelTitle>
            <TherapistAvatar>
              <img src={ImgTherapist} alt="Therapist"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
            </TherapistAvatar>
            <MessageText>
              {therapist?.therapist_message || t("patientHome.noMessage")}
            </MessageText>
            <PanelButton onClick={() => history.push(pathPatientCaregiverSupport)}>
              {t("patientHome.caregiverSupport")}
            </PanelButton>
          </Panel>

          <PanelWide>
            <PanelTitle>{t("patientHome.roadtrip")}</PanelTitle>
            <Roadtrip />
          </PanelWide>

          <Panel>
            <PanelTitle>{t("weeklyForm.title")}</PanelTitle>
            {weeklyCard && (
              <>
                <QuestionnaireIcon>
                  <img src={weeklyCard.img} alt="" style={{ width: 96, objectFit: "contain" }} />
                </QuestionnaireIcon>
                <QuestionnaireTitle>{t(`weeklyForm.${weeklyCard.title}`)}</QuestionnaireTitle>
                <QuestionnaireText>{t(`weeklyForm.${weeklyCard.text}`)}</QuestionnaireText>
                <PanelButton
                  disabled={weeklyCard.disabled}
                  $outline={weeklyCard.outline}
                  onClick={() => setShowWeeklyForm(true)}
                >
                  {t(`weeklyForm.${weeklyCard.button}`)}
                </PanelButton>
              </>
            )}
          </Panel>
        </BottomRow>
      </Main>

      {showWeeklyForm && (
        <WeeklyFormModal
          state={weeklyForm}
          onClose={() => setShowWeeklyForm(false)}
          onSaved={() => setShowWeeklyForm(false)}
        />
      )}
    </Layout>
  );
};

export default PatientHome;
